import React, { useMemo, useState } from 'react';
import {
  Badge, Button, Card, CardHeader, EmptyState, Sparkline, Table, Tabs, Tooltip,
} from '@opendate-app/setlist';
import {
  daysAgo, formatCount, formatPct, formatSignedPct, signedColor,
} from './bookingUtils';
import sortRows from './sortRows';

export const RELATIONSHIP_TABS = [
  { value: 'agent', label: 'Agent' },
  { value: 'agency', label: 'Agency' },
];
const COLLAPSED_ROWS = 10;
const FLAGS = {
  growing: { label: 'Growing', color: 'success', rule: 'Growing means offers rose 30% or more year over year.' },
  cooling: { label: 'Cooling', color: 'warning', rule: 'Cooling means offers fell 30% or more year over year.' },
};

export function flagTooltip(row) {
  const flag = FLAGS[row.flag];
  const recent = row.offers_last_12_months;
  const prior = row.offers_prior_12_months;
  const offers = `${formatCount(recent)} ${recent === 1 ? 'offer' : 'offers'} in the last 12 months`;
  if (!prior) return { title: `${offers}, up from none the 12 months before`, supportingText: flag.rule };
  const pct = Math.round((Math.abs(recent - prior) / prior) * 100);
  const direction = recent >= prior ? 'up' : 'down';
  return {
    title: `${offers}, ${direction} ${pct}% from ${formatCount(prior)} the 12 months before`,
    supportingText: flag.rule,
  };
}

function FlagBadge({ row }) {
  const flag = FLAGS[row.flag];
  if (!flag) return null;
  return (
    <Tooltip {...flagTooltip(row)}>
      <span tabIndex={0}>
        <Badge size="sm" color={flag.color}>{flag.label}</Badge>
      </span>
    </Tooltip>
  );
}

function statTiles(stats, by) {
  const noun = by === 'agency' ? 'agencies' : 'agents';
  return [
    { label: `Active ${noun}`, value: formatCount(stats.active), note: `+${formatCount(stats.new_this_period)} new vs prior 12 months` },
    { label: 'Top 3 share of confirms', value: formatPct(stats.top_three_share), note: `vs ${formatPct(stats.top_three_share_last_year)} LY` },
    { label: 'Relationships growing', value: formatCount(stats.growing), note: 'offers up 30%+ YoY' },
    { label: 'Relationships cooling', value: formatCount(stats.cooling), note: 'offers down 30%+ YoY' },
  ];
}

function columns(by) {
  const num = (key, format = formatCount) => ({ key, align: 'right', sortable: true, render: (row) => format(row[key]) });
  return [
    {
      key: 'name',
      header: by === 'agency' ? 'Agency' : 'Agent',
      primary: true,
      sortable: true,
      render: (row) => (
        <div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <span style={{ fontWeight: 600, color: 'var(--sl-text-primary)' }}>{row.name}</span>
            <FlagBadge row={row} />
          </div>
          {row.sub && <div style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>{row.sub}</div>}
        </div>
      ),
    },
    { ...num('holds'), header: 'Holds' },
    { ...num('offers'), header: 'Offers' },
    { ...num('confirms'), header: 'Confirms' },
    { ...num('hold_to_confirm_rate', formatPct), header: 'Hold → confirm' },
    {
      key: 'confirms_yoy',
      header: 'Confirms YoY',
      align: 'right',
      sortable: true,
      render: (row) => <span style={{ color: signedColor(row.confirms_yoy) }}>{formatSignedPct(row.confirms_yoy)}</span>,
    },
    {
      key: 'offers_by_month',
      header: 'Offers, 12 mo',
      width: 110,
      render: (row) => <Sparkline points={row.offers_by_month} height={24} />,
    },
    { key: 'last_offer_at', header: 'Last offer', align: 'right', sortable: true, render: (row) => daysAgo(row.last_offer_at) },
  ];
}

function BookingRelationshipsCard({ data, loading, error, by, onByChange, isMobile }) {
  const [sort, setSort] = useState({ key: 'holds', direction: 'desc' });
  const [expanded, setExpanded] = useState(false);
  const rows = useMemo(() => sortRows(data?.rows || [], sort), [data, sort]);
  const visibleRows = expanded ? rows : rows.slice(0, COLLAPSED_ROWS);
  const footer = rows.length > COLLAPSED_ROWS && (
    <div className="px-4 py-3">
      <Button variant="link-color" size="sm" onClick={() => setExpanded(!expanded)}>
        {expanded ? 'Show less' : `Show all ${formatCount(rows.length)}`}
      </Button>
    </div>
  );

  return (
    <Card
      padding="none"
      footer={footer || undefined}
      header={(
        <CardHeader
          title="Agent relationships"
          supportingText="Who your holds and offers go to, and which relationships convert. Credited through event and offer contacts."
          actions={<Tabs variant="button-border" size="sm" collapseBelow={0} items={RELATIONSHIP_TABS} value={by} onChange={onByChange} />}
        />
      )}
    >
      {data?.stats && !loading && (
        <div className="px-4 pt-3" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
          {statTiles(data.stats, by).map((stat) => (
            <div key={stat.label} style={{ padding: 12, borderRadius: 8, background: 'var(--sl-bg-secondary)' }}>
              <div style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>{stat.label}</div>
              <div className="d-flex align-items-baseline" style={{ gap: 8 }}>
                <span style={{ fontSize: 20, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{stat.value}</span>
                <span style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>{stat.note}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="pt-3">
        <Table
          columns={columns(by)}
          data={visibleRows}
          getRowKey={(row) => row.id}
          loading={loading}
          error={error}
          sort={sort}
          onSortChange={(key, direction) => setSort({ key, direction })}
          stickyFirstColumn
          empty={<EmptyState title="No agent activity in this range" description="Link agents to your holds and offers as contacts to see them here." />}
        />
      </div>
    </Card>
  );
}

export default BookingRelationshipsCard;
